import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { guardarDestino } from '@/lib/destino';
import { formatarCentavos } from '@/pages/rifa-nova/utils';

interface VendedorLinha {
  vendedor: string;
  telefone: string | null;
  codigo: string;
  taloes: string | null;
  numeros: number | null;
  cotas_vendidas: number | null;
  ainda_falta: string | null;
  quitada: boolean;
}

function formatarData(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}/${d.getFullYear()}`;
}

function fraseSorteio(metodo: string | null, sorteioEm: string | null): string {
  const rotulo =
    metodo === 'loteria_federal'
      ? 'Loteria Federal'
      : metodo === 'instagram'
        ? 'Ao vivo no Instagram'
        : metodo === 'facebook'
          ? 'Ao vivo no Facebook'
          : metodo || 'Resultado';
  const data = formatarData(sorteioEm);
  return data ? `${rotulo} · ${data}` : rotulo;
}

function telefoneWhats(telefone: string | null): string {
  const d = (telefone || '').replace(/\D/g, '');
  if (!d) return '';
  return d.startsWith('55') ? d : `55${d}`;
}

export default function RifaProntaPage() {
  const { rifaId } = useParams<{ rifaId: string }>();
  const navigate = useNavigate();
  const { user, pronto } = useAuth();
  const idNum = Number(rifaId);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [titulo, setTitulo] = useState('a ação entre amigos');
  const [valorCentavos, setValorCentavos] = useState(0);
  const [metodoSorteio, setMetodoSorteio] = useState<string | null>(null);
  const [sorteioEm, setSorteioEm] = useState<string | null>(null);
  const [premioPrincipal, setPremioPrincipal] = useState('');
  const [maisPremios, setMaisPremios] = useState(0);
  const [vendedores, setVendedores] = useState<VendedorLinha[]>([]);
  const [modoEvento, setModoEvento] = useState(false);

  const origin = useMemo(() => {
    const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
    const pathPrefix = basePath ? `/${basePath}` : '';
    return `${window.location.origin}${pathPrefix}`;
  }, []);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setCarregando(true);
      setErro(null);
      try {
        if (!pronto) return;
        if (!user) {
          guardarDestino();
          navigate(`/entrar?voltar=${encodeURIComponent(`/sorteio/${rifaId}/pronta`)}`, {
            replace: true,
          });
          return;
        }

        const { data: rifa, error: errRifa } = await supabase
          .from('rifas')
          .select(
            'id, evento_id, evento_beneficiado_id, valor_cota_centavos, metodo_sorteio, sorteio_em',
          )
          .eq('id', idNum)
          .maybeSingle();
        if (errRifa) throw errRifa;
        if (!rifa) {
          if (ativo) setErro('Ação entre amigos não encontrada.');
          return;
        }

        const r = rifa as {
          evento_id: number;
          evento_beneficiado_id: number | null;
          valor_cota_centavos: number;
          metodo_sorteio: string | null;
          sorteio_em: string | null;
        };

        const tituloId = r.evento_beneficiado_id ?? r.evento_id;

        const [resPremios, resVendedores, resEvento] = await Promise.all([
          supabase.from('rifa_premios').select('descricao').eq('rifa_id', idNum).order('id'),
          supabase
            .from('vw_rifa_vendedor')
            .select('vendedor, telefone, codigo, taloes, numeros, cotas_vendidas, ainda_falta, quitada')
            .eq('rifa_id', idNum)
            .order('vendedor'),
          supabase.from('eventos').select('nome').eq('id', tituloId).maybeSingle(),
        ]);
        if (resPremios.error) throw resPremios.error;
        if (resVendedores.error) throw resVendedores.error;
        if (resEvento.error) throw resEvento.error;

        const premios = (resPremios.data as { descricao: string }[]) || [];
        const principal = premios[0]?.descricao || '';
        const extras = Math.max(0, premios.length - 1);

        if (ativo) {
          setValorCentavos(r.valor_cota_centavos);
          setMetodoSorteio(r.metodo_sorteio);
          setSorteioEm(r.sorteio_em);
          setPremioPrincipal(principal);
          setMaisPremios(extras);
          setVendedores((resVendedores.data as VendedorLinha[]) || []);
          setModoEvento(!!r.evento_beneficiado_id);
          setTitulo((resEvento.data as { nome: string } | null)?.nome || 'a ação entre amigos');
        }
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [rifaId, idNum, navigate, pronto, user]);

  function textoWhats(v: VendedorLinha): string {
    const numeros = v.numeros != null ? String(v.numeros) : '';
    const faixa = v.taloes || '';
    const linhas = [
      `🎟️ Seu talão da ação entre amigos ${titulo}`,
      `Você tem ${numeros} números: ${faixa}. Cada um custa ${formatarCentavos(valorCentavos)}.`,
    ];
    if (modoEvento) {
      linhas.push('Vendeu o talão inteiro → sua inscrição está paga.');
    }
    linhas.push(
      'Abra aqui e mostre o QR para quem for comprar:',
      `${origin}/t/${v.codigo}`,
    );
    return linhas.join('\n');
  }

  function abrirWhatsApp(v: VendedorLinha) {
    const numero = telefoneWhats(v.telefone);
    if (!numero) return;
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(textoWhats(v))}`, '_blank', 'noopener,noreferrer');
  }

  function enviarParaTodos() {
    const comTelefone = vendedores.filter((v) => telefoneWhats(v.telefone));
    comTelefone.forEach((v, i) => {
      window.setTimeout(() => abrirWhatsApp(v), i * 800);
    });
  }

  if (carregando) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[880px] px-4 py-8">
          <div className="h-8 w-56 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 h-64 w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-8 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Não foi possível carregar</h1>
            <p className="mt-2 text-sm text-secondary-500">{erro}</p>
            <button
              type="button"
              onClick={() => navigate('/minhas-rifas')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <div className="mx-auto w-full max-w-[880px] px-4 py-8">
        {/* Topo */}
        <div className="text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500 text-white">
            <i className="ri-check-line text-4xl" />
          </span>
          <h1 className="mt-4 text-2xl font-normal text-foreground-950">AÇÃO ENTRE AMIGOS PUBLICADA</h1>
          <p className="mt-2 text-[15px] text-foreground-800">
            {premioPrincipal}
            {maisPremios > 0 && ` (e mais ${maisPremios} ${maisPremios === 1 ? 'prêmio' : 'prêmios'})`}
          </p>
          <p className="mt-1 text-sm text-secondary-500">{fraseSorteio(metodoSorteio, sorteioEm)}</p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Talões repartidos */}
          <section className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
            <div className="h-[10px] w-full bg-primary-500" />
            <div className="p-6">
              <h2 className="text-base font-medium text-foreground-950">Talões repartidos</h2>
              <p className="mt-0.5 text-sm text-secondary-500">
                Cada vendedor recebeu a sua faixa de números.
              </p>

              {vendedores.length === 0 ? (
                <p className="mt-4 py-4 text-sm text-secondary-500">
                  Nenhum talão repartido ainda.
                </p>
              ) : (
                <ul className="mt-4 divide-y divide-secondary-100">
                  {vendedores.map((v, i) => (
                    <li key={i} className="py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[15px] font-medium text-foreground-900">{v.vendedor}</p>
                          <p className="mt-0.5 text-sm text-secondary-500">
                            {v.taloes ? v.taloes : <span className="text-secondary-400">sem talão ainda</span>}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => abrirWhatsApp(v)}
                          disabled={!telefoneWhats(v.telefone)}
                          className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <span className="flex h-4 w-4 items-center justify-center text-green-600">
                            <i className="ri-whatsapp-line" />
                          </span>
                          WhatsApp
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* Ações */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={enviarParaTodos}
              disabled={vendedores.filter((v) => telefoneWhats(v.telefone)).length === 0}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="flex h-4 w-4 items-center justify-center text-green-600">
                <i className="ri-whatsapp-line" />
              </span>
              Enviar para todos no WhatsApp
            </button>

            <button
              type="button"
              onClick={() => navigate(`/sorteio/${rifaId}/imprimir`)}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
            >
              <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                <i className="ri-printer-line" />
              </span>
              Imprimir talões
            </button>

            <button
              type="button"
              onClick={() => navigate(`/sorteio/${rifaId}`)}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Ir para o painel da ação entre amigos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}